import React, { useState, useEffect, useContext } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Compass, Search, Clock, CreditCard, LogOut, Crown, History, Settings, ChevronRight, Loader2, Home, ChevronLeft, Trash2, Key, Star, MessageSquare, FileText, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AuthContext, API } from "@/App";
import { toast } from "sonner";
import { NotificationsBell } from "@/components/NotificationsBell";

const Dashboard = () => {
  const { user, logout, checkAuth } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    fetchData();
    const sessionId = searchParams.get("session_id");
    const plan = searchParams.get("plan");
    if (sessionId) pollPaymentStatus(sessionId, plan);
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [statsRes, searchesRes] = await Promise.all([
        fetch(`${API}/user/stats`, { credentials: "include" }),
        fetch(`${API}/user/searches`, { credentials: "include" })
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (searchesRes.ok) setSearches(await searchesRes.json());
    } catch (e) {} finally { setLoading(false); }
  };

  const pollPaymentStatus = async (sessionId, plan, attempts = 0) => {
    if (attempts >= 5) { toast.info("Payment verification pending."); navigate("/dashboard", { replace: true }); return; }
    try {
      const res = await fetch(`${API}/checkout/status/${sessionId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.payment_status === "paid") {
          toast.success(`Welcome to ${plan?.charAt(0).toUpperCase() + plan?.slice(1)}!`);
          navigate("/dashboard", { replace: true }); fetchData(); checkAuth(); return;
        }
      }
      setTimeout(() => pollPaymentStatus(sessionId, plan, attempts + 1), 2000);
    } catch (e) { setTimeout(() => pollPaymentStatus(sessionId, plan, attempts + 1), 2000); }
  };

  const handleUpgrade = async (plan) => {
    try {
      const res = await fetch(`${API}/checkout/create`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ plan, origin_url: window.location.origin })
      });
      if (res.ok) { const data = await res.json(); window.location.href = data.url; }
      else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Cancel your subscription? You'll be moved to free tier.")) return;
    try {
      const res = await fetch(`${API}/user/cancel-subscription`, { method: "POST", credentials: "include" });
      if (res.ok) { toast.success("Subscription cancelled"); fetchData(); checkAuth(); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    if (newPassword.length < 6) { toast.error("Min 6 characters"); return; }
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ current_password: currentPassword || null, new_password: newPassword })
      });
      if (res.ok) { toast.success("Password changed!"); setShowPasswordDialog(false); setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); }
      else { const e = await res.json(); toast.error(e.detail || "Failed"); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleDeleteAccount = async () => {
    try {
      const res = await fetch(`${API}/auth/delete-account`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Account deleted"); logout(); navigate("/"); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const getSearchLimit = () => {
    if (!stats) return { used: 0, total: 10, percentage: 0 };
    const total = stats.searches_limit === -1 ? "\u221e" : stats.searches_limit;
    const percentage = stats.searches_limit === -1 ? 100 : Math.min((stats.searches_this_month / stats.searches_limit) * 100, 100);
    return { used: stats.searches_this_month, total, percentage };
  };

  const searchLimit = getSearchLimit();
  const isLimitReached = stats?.searches_limit !== -1 && stats?.searches_this_month >= stats?.searches_limit;

  if (loading) return <div className="min-h-screen bg-dark flex items-center justify-center"><Loader2 className="w-8 h-8 text-purple-400 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-dark">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white/60 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => navigate(1)} className="text-white/60 hover:text-white"><ChevronRight className="w-5 h-5" /></Button>
              </div>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-700 flex items-center justify-center"><Compass className="w-4 h-4 text-white" /></div>
                <span className="font-inter font-bold text-lg">promptviser</span>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <NotificationsBell />
              <Link to="/"><Button variant="ghost" size="icon" className="text-white/60 hover:text-white"><Home className="w-5 h-5" /></Button></Link>
              <Link to="/"><Button variant="outline" size="sm" className="border-white/20 text-xs"><Search className="w-3.5 h-3.5 mr-1" />New Search</Button></Link>
              {(user?.is_admin || user?.subscription_tier === "team" || user?.team_id) && (
                <Link to="/team"><Button variant="outline" size="sm" className="border-neon/30 text-neon text-xs" data-testid="team-nav-btn">Team</Button></Link>
              )}
              {(user?.is_admin || user?.is_vip || ["pro","premium","team"].includes(user?.subscription_tier)) && (
                <Link to="/chat"><Button variant="outline" size="sm" className="border-purple-500/40 text-purple-300 text-xs"><MessageSquare className="w-3.5 h-3.5 mr-1" />Chat</Button></Link>
              )}
              {user?.is_admin && <Link to="/admin"><Button variant="outline" size="sm" className="border-purple-500/50 text-purple-300 text-xs">Admin</Button></Link>}
              <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} className="text-white/60 text-xs"><LogOut className="w-3.5 h-3.5 mr-1" />Logout</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="mb-8">
          <h1 className="font-inter font-bold text-2xl mb-1">
            {user?.login_count <= 1 ? "Welcome" : "Welcome back"}, {user?.name?.split(" ")[0] || "User"}
            {user?.is_vip && <Star className="inline w-5 h-5 text-neon ml-2" />}
          </h1>
          <p className="text-white/40 text-sm">Manage your searches and subscription</p>
        </div>

        {user?.email_verified === false && (
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="verify-email-banner">
            <p className="text-sm text-white/60">📬 Please verify your email to unlock paid upgrades. Check your inbox for the verification link.</p>
            <Button
              onClick={async () => {
                const res = await fetch(`${API}/auth/resend-verification`, {
                  method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
                  body: JSON.stringify({ origin_url: window.location.origin })
                });
                if (res.ok) toast.success("Verification email sent!");
                else toast.error("Failed to send");
              }}
              variant="outline" size="sm" className="border-purple-500/40 text-purple-300 text-xs whitespace-nowrap" data-testid="resend-verification-btn"
            >Resend verification email</Button>
          </div>
        )}


        {isLimitReached && stats?.subscription_tier === "free" && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5 mb-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-amber-400 text-sm mb-0.5">Monthly Search Limit Reached</h3>
                <p className="text-white/50 text-xs">You've used all 10 free searches. Resets 30 days after exhaustion.</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handleUpgrade("pro")} className="bg-purple-700 text-white hover:bg-purple-600 text-xs">Upgrade to Pro</Button>
                <Button onClick={() => navigate("/pricing")} variant="outline" className="border-white/20 text-xs">View Plans</Button>
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-dark-paper border border-white/10 rounded-xl p-5" data-testid="search-usage-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-purple-900/50 flex items-center justify-center"><Search className="w-4 h-4 text-purple-400" /></div>
              <div>
                <p className="text-xs text-white/40">Searches This Month</p>
                <p className="font-inter font-bold text-xl">{searchLimit.used} / {searchLimit.total}</p>
              </div>
            </div>
            {stats?.searches_limit !== -1 && <Progress value={searchLimit.percentage} className="h-1.5" />}
            <p className="text-[10px] mt-1.5 text-white/30">Prompt tier: <span className="text-purple-300 capitalize">{stats?.prompt_tier || "basic"}</span></p>
          </div>

          <div className="bg-dark-paper border border-white/10 rounded-xl p-5" data-testid="subscription-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-purple-900/50 flex items-center justify-center"><Crown className="w-4 h-4 text-purple-400" /></div>
              <div>
                <p className="text-xs text-white/40">Current Plan</p>
                <div className="flex items-center gap-2">
                  <p className="font-inter font-bold text-xl capitalize">{stats?.subscription_tier || "Free"}</p>
                  {stats?.is_vip && <Badge className="bg-neon/10 text-neon text-[9px]">VIP</Badge>}
                </div>
              </div>
            </div>
            {stats?.subscription_tier === "free" && !stats?.is_vip && (
              <Button onClick={() => handleUpgrade("pro")} className="w-full bg-purple-700 text-white hover:bg-purple-600 text-xs font-semibold" data-testid="upgrade-button">Upgrade to Pro</Button>
            )}
          </div>

          <div className="bg-dark-paper border border-white/10 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-purple-900/50 flex items-center justify-center"><Settings className="w-4 h-4 text-purple-400" /></div>
              <div><p className="text-xs text-white/40">Quick Actions</p><p className="font-semibold text-sm">Manage Account</p></div>
            </div>
            <div className="space-y-1.5">
              <Link to="/" className="flex items-center justify-between p-1.5 rounded-lg hover:bg-white/5 transition-colors"><span className="text-xs text-white/60">Start New Search</span><ChevronRight className="w-3.5 h-3.5 text-white/30" /></Link>
              {(user?.is_admin || user?.is_vip || ["premium","team"].includes(user?.subscription_tier)) && (
                <>
                  <Link to="/seo" className="flex items-center justify-between p-1.5 rounded-lg hover:bg-white/5 transition-colors"><span className="text-xs text-white/60 flex items-center gap-1.5"><FileText className="w-3 h-3" />SEO Tools</span><ChevronRight className="w-3.5 h-3.5 text-white/30" /></Link>
                  <Link to="/news" className="flex items-center justify-between p-1.5 rounded-lg hover:bg-white/5 transition-colors"><span className="text-xs text-white/60 flex items-center gap-1.5"><Newspaper className="w-3 h-3" />News Feed</span><ChevronRight className="w-3.5 h-3.5 text-white/30" /></Link>
                </>
              )}
              <Link to="/pricing" className="flex items-center justify-between p-1.5 rounded-lg hover:bg-white/5 transition-colors"><span className="text-xs text-white/60">View Plans</span><ChevronRight className="w-3.5 h-3.5 text-white/30" /></Link>
              <button onClick={() => setShowPasswordDialog(true)} className="w-full flex items-center justify-between p-1.5 rounded-lg hover:bg-white/5 transition-colors"><span className="text-xs text-white/60">Change Password</span><Key className="w-3.5 h-3.5 text-white/30" /></button>
            </div>
          </div>
        </div>

        <Tabs defaultValue="history" className="w-full">
          <TabsList className="bg-dark-paper border border-white/10 p-1">
            <TabsTrigger value="history" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs"><History className="w-3.5 h-3.5 mr-1" />History</TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs"><CreditCard className="w-3.5 h-3.5 mr-1" />Billing</TabsTrigger>
            <TabsTrigger value="account" className="data-[state=active]:bg-purple-700 data-[state=active]:text-white text-xs"><Settings className="w-3.5 h-3.5 mr-1" />Account</TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="mt-6">
            {searches.length === 0 ? (
              <div className="text-center py-12 bg-dark-paper border border-white/10 rounded-xl">
                <Search className="w-10 h-10 text-white/15 mx-auto mb-3" />
                <h3 className="font-semibold mb-1">No searches yet</h3>
                <p className="text-white/40 text-sm mb-3">Start searching to build your history</p>
                <Link to="/"><Button className="bg-purple-700 text-white hover:bg-purple-600 text-xs">Start Searching</Button></Link>
              </div>
            ) : (
              <div className="space-y-3">
                {searches.map((search, index) => (
                  <div key={search.search_id || index} className="bg-dark-paper border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors" data-testid={`search-history-${index}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="font-mono text-purple-300 text-sm mb-1.5">"{search.prompt}"</p>
                        <div className="flex flex-wrap gap-1.5">
                          {search.results?.slice(0, 3).map((r, i) => (
                            <Badge key={i} variant="secondary" className="text-[10px]">{r.tool_name} - {r.match_percentage}%</Badge>
                          ))}
                          {search.results?.length > 3 && <Badge variant="outline" className="text-[10px] border-white/20">+{search.results.length - 3} more</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-white/30"><Clock className="w-3 h-3" />{new Date(search.created_at).toLocaleDateString()}</div>
                    </div>
                    <Link to={`/search?q=${encodeURIComponent(search.prompt)}`} className="inline-flex items-center gap-1 text-xs text-purple-400 mt-2 hover:underline">Search again<ChevronRight className="w-3.5 h-3.5" /></Link>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="billing" className="mt-6">
            <div className="bg-dark-paper border border-white/10 rounded-xl p-5">
              <h3 className="font-inter font-semibold mb-4">Subscription</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/50">Plan</span><span className="font-semibold capitalize">{stats?.subscription_tier || "Free"}</span></div>
                <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/50">Searches/Month</span><span className="font-semibold">{stats?.searches_limit === -1 ? "Unlimited" : stats?.searches_limit || 10}</span></div>
                <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/50">Prompt Tier</span><span className="font-semibold capitalize">{stats?.prompt_tier || "Basic"}</span></div>
                {stats?.subscription_billing && (
                  <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/50">Billing Cycle</span><span className="font-semibold capitalize" data-testid="billing-cycle">{stats.subscription_billing}</span></div>
                )}
                {stats?.subscription_started_at && stats?.subscription_tier !== "free" && (
                  <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/50">Subscribed On</span><span className="font-semibold" data-testid="subscription-start-date">{new Date(stats.subscription_started_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span></div>
                )}
                {stats?.subscription_expires_at && stats?.subscription_tier !== "free" && (
                  <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/50">Renews / Expires</span><span className="font-semibold text-neon" data-testid="subscription-expiry-date">{new Date(stats.subscription_expires_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span></div>
                )}
              </div>
              {stats?.subscription_tier === "free" && !stats?.is_vip ? (
                <div className="mt-5 p-4 bg-purple-900/20 border border-purple-500/20 rounded-lg">
                  <h4 className="font-semibold text-sm mb-1">Upgrade Your Plan</h4>
                  <p className="text-xs text-white/50 mb-3">Get unlimited searches and advanced prompts</p>
                  <div className="flex gap-2">
                    <Button onClick={() => handleUpgrade("pro")} className="bg-purple-700 text-white hover:bg-purple-600 text-xs font-semibold">Pro - $15/mo</Button>
                    <Button onClick={() => handleUpgrade("premium")} variant="outline" className="border-white/20 text-xs">Premium - $35/mo</Button>
                  </div>
                </div>
              ) : stats?.subscription_tier !== "free" && (
                <div className="mt-5">
                  <Button onClick={handleCancelSubscription} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs">Cancel Subscription</Button>
                  <p className="text-[10px] text-white/30 mt-1.5">Cancel anytime. Access continues until billing period ends.</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="account" className="mt-6">
            <div className="bg-dark-paper border border-white/10 rounded-xl p-5">
              <h3 className="font-inter font-semibold mb-4">Account Settings</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/50">Email</span><span className="font-semibold">{user?.email}</span></div>
                <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/50">Name</span><span className="font-semibold">{user?.name}</span></div>
                <div className="flex justify-between py-2 border-b border-white/5 text-sm"><span className="text-white/50">Member Since</span><span className="font-semibold">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</span></div>
              </div>
              <div className="mt-5 space-y-2">
                <Button onClick={() => setShowPasswordDialog(true)} variant="outline" className="w-full border-white/20 text-xs"><Key className="w-3.5 h-3.5 mr-1" />Change Password</Button>
                <Button onClick={() => setShowDeleteDialog(true)} variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete Account</Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-dark-paper border-white/10">
          <DialogHeader><DialogTitle className="text-sm">Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div><Label className="text-xs">Current Password</Label><Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="bg-dark-subtle border-white/10 mt-1 text-sm" placeholder="Leave empty if Google" /></div>
            <div><Label className="text-xs">New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="bg-dark-subtle border-white/10 mt-1 text-sm" /></div>
            <div><Label className="text-xs">Confirm</Label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="bg-dark-subtle border-white/10 mt-1 text-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="border-white/20 text-xs">Cancel</Button>
            <Button onClick={handleChangePassword} className="bg-purple-700 text-white hover:bg-purple-600 text-xs">Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-dark-paper border-white/10">
          <DialogHeader><DialogTitle className="text-red-400 text-sm">Delete Account</DialogTitle><DialogDescription className="text-xs">This action cannot be undone. All data will be permanently deleted.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-white/20 text-xs">Cancel</Button>
            <Button onClick={handleDeleteAccount} className="bg-red-500 text-white hover:bg-red-600 text-xs"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
